// Columna derecha de Control MDM: la ficha del equipo — hero con sus referencias y dos pestañas
// (Panel de control · Bitácora). Presentacional: el estado y los callbacks los pone MdmPage.
import type { ReactNode } from 'react';
import { CreditCard, Smartphone, Users } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail/MasterDetail';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import {
    deviceHardwareLabel,
    explainMissingHeartbeat,
    MDM_HEARTBEAT_MEANING,
    NO_VALUE,
    providerContextLabel,
    readHeartbeat,
    readLockPhase,
} from '../logic';
import type { MdmAction } from '../mdmActions';
import type { MdmAbilities } from '../permissions';
import type { MdmActionRejection, MdmAuditEntry, MdmFleetDevice } from '../types';
import { MdmAuditTab } from './MdmAuditTab';
import { MdmControlPanelTab, type MdmRefreshControl } from './MdmControlPanelTab';
import { MdmStatusPill } from './MdmStatusPill';

export const MDM_DETAIL_TABS = ['panel', 'audit'] as const;
export type MdmDetailTab = (typeof MDM_DETAIL_TABS)[number];

/** Un valor desconocido cae en «panel» en vez de dejar la ficha en blanco. */
export function parseMdmDetailTab(raw: string | undefined): MdmDetailTab {
    return MDM_DETAIL_TABS.includes(raw as MdmDetailTab) ? (raw as MdmDetailTab) : 'panel';
}

const TAB_LABEL: Record<MdmDetailTab, string> = {
    panel: 'Panel de control',
    audit: 'Bitácora',
};

// El portal no tiene ficha 360 con enlace directo: estas salidas abren la sección del sidebar.
const SECTION_HINT = 'Abre la sección del portal. Todavía no hay ficha con enlace directo.';

export interface MdmDeviceDetailProps {
    /** `null` pinta el estado vacío de la columna. */
    device: MdmFleetDevice | null;
    abilities: MdmAbilities;
    /** Instante de referencia, fijo mientras la pantalla está abierta. */
    now: Date | string;
    auditEntries: readonly MdmAuditEntry[];
    /** Estado de la lectura de la bitácora: la pestaña separa cargando, error y vacío legítimo. */
    auditIsLoading?: boolean;
    auditError?: Error | null;
    tab: MdmDetailTab;
    onTabChange: (tab: MdmDetailTab) => void;
    /** La consulta al proveedor: el botón del encabezado del panel y su resultado. */
    refresh: MdmRefreshControl;
    onAction: (action: MdmAction) => void;
    /** La última orden rechazada sobre este equipo; `null` sin rechazo. */
    actionRejection?: MdmActionRejection | null;
    onDismissRejection?: () => void;
}

const REF_LABEL = 'text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

function SectionLink({
    section,
    label,
    children,
}: {
    section: string;
    label: string;
    children: ReactNode;
}) {
    return (
        <Link
            to="/credit/$section"
            params={{ section }}
            title={`${label} · ${SECTION_HINT}`}
            aria-label={label}
            className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
            {children}
        </Link>
    );
}

export function MdmDeviceDetail({
    device,
    abilities,
    now,
    auditEntries,
    auditIsLoading = false,
    auditError = null,
    tab,
    onTabChange,
    refresh,
    onAction,
    actionRejection = null,
    onDismissRejection,
}: MdmDeviceDetailProps) {
    if (!device) {
        return (
            <MasterDetailEmpty
                icon={<Smartphone className="size-10" aria-hidden />}
                title="Selecciona un dispositivo"
                sub="Elige un dispositivo de la lista para ver su estado, bitácora y operar sobre él con las acciones del proveedor."
            />
        );
    }

    const heartbeat = readHeartbeat(device.lastSeenAt, now, device.mdmProvider);
    const lockPhase = readLockPhase(device, now);
    const auditDisabled = !abilities.canViewOperationLog;
    const shownTab: MdmDetailTab = tab === 'audit' && auditDisabled ? 'panel' : tab;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="border-b border-border px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-[15px] font-bold leading-tight">
                        {deviceHardwareLabel(device)}
                    </h2>
                    <MdmStatusPill
                        status={device.mdmStatus}
                        mdmConfirmed={device.mdmConfirmed}
                        size="md"
                        lockPhase={lockPhase}
                        lockScheduledFor={device.currentLockScheduledFor}
                    />
                    <span className="text-[10.5px] font-semibold tracking-[0.02em] text-muted-foreground">
                        {providerContextLabel(device)}
                    </span>
                </div>

                {/* `auto-fit` reparte por el ancho del PANEL; `sm:` medía la ventana y a 1280 dejaba 93 px. */}
                <div className="mt-2.5 grid grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1">
                            <span className={REF_LABEL}>Cliente</span>
                            <SectionLink section="customers" label="Ver en la sección Clientes">
                                <Users className="size-3" aria-hidden />
                            </SectionLink>
                        </div>
                        {/* El nombre baja de renglón antes que recortarse: es la referencia que el operador busca. */}
                        <div className="break-words text-[12px] font-semibold leading-tight">
                            {device.customerName ?? NO_VALUE}
                        </div>
                        <div className="truncate text-[10.5px] text-muted-foreground">
                            {/* Clave de cliente legible (`CL-1188`); el nombre ya va resuelto arriba. */}
                            {device.customerId ?? 'El expediente del equipo no guarda al cliente'}
                        </div>
                    </div>

                    <div className="min-w-0">
                        <div className="flex items-center gap-1">
                            <span className={REF_LABEL}>Crédito</span>
                            <Link
                                to="/credit/portfolio"
                                search={{ credit: device.creditId }}
                                title="Abrir el expediente de este crédito en Cartera"
                                aria-label="Ver el crédito en Cartera"
                                className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                <CreditCard className="size-3" aria-hidden />
                            </Link>
                        </div>
                        <div className="truncate font-mono text-[12px] font-semibold">
                            {device.creditRef}
                        </div>
                    </div>

                    {/* El IMEI completo vive aquí: en la reja de métricas nunca cabía y salía cortado. */}
                    <div className="min-w-0">
                        <div className={REF_LABEL}>IMEI</div>
                        <div className="truncate font-mono text-[12px] font-semibold">
                            {device.imei}
                        </div>
                    </div>

                    <div className="min-w-0">
                        <div
                            className={REF_LABEL}
                            title={MDM_HEARTBEAT_MEANING[device.mdmProvider]}
                        >
                            Última señal del equipo
                        </div>
                        <div className="truncate text-[12px] font-semibold tabular-nums">
                            {device.lastSeenAt ? formatDateTimeMx(device.lastSeenAt) : NO_VALUE}
                        </div>
                        <div className="text-[10.5px] leading-snug text-muted-foreground">
                            {device.lastSeenAt
                                ? heartbeat.longLabel
                                : explainMissingHeartbeat(device.mdmStatus, device.mdmProvider)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex gap-1 border-b border-border px-4" role="tablist">
                {/* Sin permiso la pestaña Bitácora no se pinta. */}
                {MDM_DETAIL_TABS.filter((key) => key !== 'audit' || !auditDisabled).map((key) => {
                    const active = key === shownTab;
                    return (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => {
                                onTabChange(key);
                            }}
                            className={cn(
                                'border-b-2 px-3 py-2 text-[12px] font-medium transition-colors',
                                active
                                    ? 'border-primary text-foreground'
                                    : 'border-transparent text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {TAB_LABEL[key]}
                        </button>
                    );
                })}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {shownTab === 'audit' ? (
                    <MdmAuditTab
                        device={device}
                        entries={auditEntries}
                        isLoading={auditIsLoading}
                        error={auditError}
                    />
                ) : (
                    <MdmControlPanelTab
                        device={device}
                        abilities={abilities}
                        now={now}
                        refresh={refresh}
                        onAction={onAction}
                        actionRejection={actionRejection}
                        onDismissRejection={onDismissRejection}
                    />
                )}
            </div>
        </div>
    );
}
